import { useSecondModal } from '../../../../providers/SecondModalProvider/SecondModalProvider'
import { useModals } from '../../../../providers/ModalsProvider/ModalsProvider'
import useSeatmapStore, {
  SEAT_VIEW_PLACEHOLDER,
} from '../../../../../../../stores/seatmapStore'
import useFiltersStore from '../../../../../../../stores/filtersStore'
import {
  formatOfferListPrice,
  getSinglarOrPluralWord,
} from '../../../../../../../utilities/helpers'
import Stack from 'react-bootstrap/Stack'
import Badge from 'react-bootstrap/Badge'

import './ticketGroup.scss'

export default function TicketGroup({ pick, venue }) {
  const getTicketImage = useSeatmapStore((state) => state.getTicketImage)
  const quantity = useFiltersStore((state) => state.filters.quantity)
  const { openModal } = useSecondModal()
  const { openCBModal, setViewingSection, setSectionNumber } = useModals()

  let offerName
  let inventoryType
  if (!Boolean(pick?.listing)) {
    offerName = pick.offer?.name
    inventoryType = pick.offer?.inventoryType
  }

  const showTicketView = (e) => {
    e.stopPropagation()
    setViewingSection(pick?.sectionId)
    setSectionNumber(pick?.sectionNumber)
    openCBModal('Your view')
  }

  const clickTicketGroup = () => {
    openModal(pick, quantity)
  }

  const getIconClass = (selectedObject) => {
    if (selectedObject?.accessible) return 'accessible'
    if (selectedObject?.GA) return 'ticket'
    return 'seat'
  }

  const iconClass = getIconClass(pick)
  const availableCount = pick.GA
    ? pick.availableCount
    : Array.isArray(pick.seatIds)
      ? pick.seatIds.length
      : 0

  return (
    <button type="button" className="spp-ticket-row" onClick={clickTicketGroup}>
      <div
        className="spp-ticket-row__thumb"
        onClick={showTicketView}
        onKeyDown={() => {}}
        role="presentation"
      >
        <img
          src={
            getTicketImage(
              venue?.slug,
              String(pick?.sectionNumber || '').toLowerCase(),
              'thumbnail'
            ) || SEAT_VIEW_PLACEHOLDER
          }
          alt=""
          onError={(event) => {
            event.currentTarget.onerror = null
            event.currentTarget.src = SEAT_VIEW_PLACEHOLDER
          }}
        />
      </div>
      <div className="spp-ticket-row__body">
        <QuickpickBadge
          isListing={Boolean(pick?.listing)}
          listing={pick?.listing}
          inventoryType={inventoryType}
          offerName={offerName}
        />
        <h3 className={`spp-ticket-row__seat ${iconClass}`}>
          {pick?.GA
            ? `${pick?.sectionName || pick?.sectionNumber || 'GA'}`
            : `Sec ${pick?.sectionNumber} · Row ${pick?.rowNumber}`}
        </h3>
        <POTicketQuantity
          availableCount={availableCount}
          offer={pick?.offer}
          listing={pick?.listing}
        />
      </div>
      <div className="spp-ticket-row__price">
        <span className="spp-ticket-row__amount">
          {formatOfferListPrice(pick?.price, pick?.offer)} each
        </span>
        <span className="spp-ticket-row__fees">
          {pick?.offer?.freeOffer
            ? 'Complimentary'
            : pick?.offer?.feesOnTop
              ? 'Ticket price'
              : 'Incl. Taxes & Fees'}
        </span>
      </div>
    </button>
  )
}

function POTicketQuantity({ availableCount, offer, listing }) {
  const eventTicketLimit = useFiltersStore((state) => state.eventTicketLimit)

  const getTicketText = () => {
    if (listing) {
      return {
        ticketText: `${listing.quantity} - ${listing.quantity}`,
        quantity: listing.quantity,
      }
    }
    if (!offer) {
      return {
        ticketText: `1 - ${availableCount}`,
        quantity: availableCount,
      }
    }
    const max =
      eventTicketLimit !== null
        ? Math.min(eventTicketLimit, availableCount)
        : availableCount
    if (offer.limit) {
      return {
        ticketText: `${offer.limit} - ${offer.limit}`,
        quantity: offer.limit,
      }
    }
    if (offer.maxQuantity) {
      const upperLimit = Math.min(offer.maxQuantity, max)
      return { ticketText: `1 - ${upperLimit}`, quantity: upperLimit }
    }
    if (offer.minQuantity) {
      return {
        ticketText: `${offer.minQuantity} - ${max}`,
        quantity: offer.minQuantity,
      }
    }
    if (offer.multipleOf) {
      return {
        ticketText: `Multiple of ${offer.multipleOf} up to ${max}`,
        quantity: offer.multipleOf,
      }
    }
    return { ticketText: `1 - ${max}`, quantity: max }
  }

  const { ticketText, quantity } = getTicketText()

  return (
    <p className="spp-ticket-row__qty">
      {ticketText} {getSinglarOrPluralWord(quantity)}
    </p>
  )
}

export function QuickpickBadge({
  isListing,
  listing,
  inventoryType,
  offerName,
}) {
  let listingOffer
  if (isListing && listing) listingOffer = listing?.offer

  const badgeText = offerName || listingOffer?.name
  const isOpen = (inventoryType || listingOffer?.inventoryType) === 'open'

  return (
    <Stack direction="horizontal" gap={2} className="spp-ticket-row__badges">
      <Badge
        bg="default"
        className={`spp-offer-badge ${isOpen ? 'is-open' : 'is-exclusive'}`}
      >
        {!isOpen ? <ExclusiveSvg /> : <span aria-hidden>★</span>}
        {badgeText}
      </Badge>
      {isListing ? (
        <Badge bg="default" className="spp-offer-badge is-resale">
          Verified Resale
        </Badge>
      ) : null}
    </Stack>
  )
}

function ExclusiveSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M7.3879 2.08514C7.6195 1.54844 8.38051 1.54844 8.61211 2.08514L10.0655 5.45307C10.1635 5.68032 10.3794 5.83454 10.6262 5.85361L14.3533 6.14161C14.9513 6.18781 15.1889 6.93852 14.7264 7.32038L11.9235 9.63456C11.7258 9.79779 11.6392 10.0599 11.7008 10.3088L12.5634 13.7944C12.7053 14.3676 12.0869 14.8286 11.578 14.529L8.33822 12.6217C8.12949 12.4988 7.87053 12.4988 7.66179 12.6217L4.42197 14.529C3.9131 14.8286 3.29475 14.3676 3.43661 13.7944L4.29924 10.3088C4.36083 10.0599 4.27425 9.79779 4.07654 9.63456L1.27362 7.32038C0.811112 6.93852 1.04872 6.18781 1.64671 6.14161L5.37381 5.85361C5.62058 5.83454 5.83648 5.68032 5.93455 5.45307L7.3879 2.08514Z"
        fill="currentColor"
      />
    </svg>
  )
}
